import {
  IPrognoz2040,
  IPrognoz2040HorizonSignal,
  IPrognoz2040QuantItem,
} from "../../../../pages/Dashboard/Dashboard.type";

export const parseModelConfidencePercent = (
  modelConfidence: string | undefined | null
): number | null => {
  if (!modelConfidence) return null;
  const match = modelConfidence.match(/(\d{1,3})%\s*$/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
};

export const resolveConfidencePercent = (
  data: Pick<IPrognoz2040, "model_confidence" | "meta"> | null | undefined
): number | null => {
  if (!data) return null;
  const fromLabel = parseModelConfidencePercent(data.model_confidence);
  if (fromLabel !== null) return fromLabel;

  const score = data.meta?.confidence_score;
  if (typeof score === "number" && Number.isFinite(score)) {
    // Spec: integer 60–100. Guard against accidental 0–1 fractions.
    const normalized = score > 0 && score <= 1 ? score * 100 : score;
    return Math.min(100, Math.max(0, Math.round(normalized)));
  }
  return null;
};

export const getConfidenceBarColor = (percent: number): string => {
  if (percent >= 90) return "#59FFAC";
  if (percent >= 80) return "#71C6FA";
  if (percent >= 70) return "#FFC107";
  return "#FF8A65";
};

export const getHorizonSignalStyle = (
  signal: IPrognoz2040HorizonSignal | undefined
): {background: string; color: string; border: string} => {
  switch (signal) {
    case "Ранняя стадия":
      return {
        background: "rgba(113, 198, 250, 0.16)",
        color: "#71C6FA",
        border: "1px solid rgba(113, 198, 250, 0.35)",
      };
    case "Масштабирование":
      return {
        background: "rgba(89, 255, 172, 0.14)",
        color: "#59FFAC",
        border: "1px solid rgba(89, 255, 172, 0.35)",
      };
    case "Массовое внедрение":
      return {
        background: "rgba(86, 179, 255, 0.18)",
        color: "#56B3FF",
        border: "1px solid rgba(86, 179, 255, 0.4)",
      };
    case "Неопределённо":
      return {
        background: "rgba(255, 193, 7, 0.12)",
        color: "#FFC107",
        border: "1px solid rgba(255, 193, 7, 0.35)",
      };
    default:
      return {
        background: "rgba(250, 250, 250, 0.12)",
        color: "rgba(255, 255, 255, 0.8)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
      };
  }
};

export const formatEvidencePages = (pages?: number[] | number | null): string => {
  if (pages == null) return "";
  const list = Array.isArray(pages)
    ? pages
    : typeof pages === "number" && Number.isFinite(pages)
    ? [pages]
    : [];
  if (list.length === 0) return "";
  const uniqueSorted = Array.from(
    new Set(list.filter((p) => Number.isFinite(p)))
  ).sort((a, b) => a - b);
  if (uniqueSorted.length === 0) return "";
  return `стр. ${uniqueSorted.join(", ")}`;
};

export const hasQuantItemContent = (
  item?: IPrognoz2040QuantItem | null
): boolean => {
  if (!item || typeof item !== "object") return false;
  return Boolean(
    item.metric?.trim() ||
      item.value?.trim() ||
      item.unit?.trim() ||
      item.note?.trim() ||
      item.region?.trim() ||
      (item.year != null && Number.isFinite(item.year))
  );
};

/** Substantive content only — horizon/confidence alone do not justify showing the section. */
export const hasPrognoz2040Content = (
  data?: IPrognoz2040 | null
): data is IPrognoz2040 => {
  if (!data || typeof data !== "object") return false;

  const hasOutlook = Boolean(data.outlook?.trim());
  const hasDrivers =
    Array.isArray(data.drivers) && data.drivers.some((d) => d?.trim());
  const hasQuant =
    Array.isArray(data.quant) && data.quant.some(hasQuantItemContent);
  const hasRussia = Boolean(data.russia_angle?.trim());
  const hasEvidence =
    Array.isArray(data.evidence) &&
    data.evidence.some((e) => e?.source?.trim());

  return hasOutlook || hasDrivers || hasQuant || hasRussia || hasEvidence;
};
